import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../i18n/context';
import { ROUTES } from '../routes/routeConfig';
import './MarketHeatmapPage.css';

const sectorMap = {
  layer1: ['bitcoin', 'ethereum', 'solana', 'binancecoin', 'cardano', 'avalanche-2', 'polkadot', 'near', 'sui', 'toncoin', 'tron', 'cosmos'],
  defi: ['uniswap', 'aave', 'chainlink', 'maker', 'lido-dao', 'jupiter-exchange-solana', 'pancakeswap-token', 'curve-dao-token', 'injective-protocol', 'thorchain'],
  meme: ['dogecoin', 'shiba-inu', 'pepe', 'dogwifcoin', 'floki', 'bonk', 'book-of-meme', 'brett', 'mog-coin', 'popcat'],
  l2: ['matic-network', 'arbitrum', 'optimism', 'starknet', 'immutable-x', 'metis-token', 'mantle'],
  gaming: ['the-sandbox', 'axie-infinity', 'decentraland', 'gala', 'illuvium', 'enjincoin', 'flow'],
  ai: ['render-token', 'fetch-ai', 'bittensor', 'akash-network', 'worldcoin-wld', 'ocean-protocol', 'singularitynet', 'ar'],
};

const stocks = [
  { symbol: 'NVDA', name: 'NVIDIA', fallbackWeight: 3.1 },
  { symbol: 'MSTR', name: 'MicroStrategy', fallbackWeight: 1.4 },
  { symbol: 'COIN', name: 'Coinbase', fallbackWeight: 1.2 },
  { symbol: 'QQQ', name: 'Nasdaq 100 ETF', fallbackWeight: 1.8 },
  { symbol: 'TSLA', name: 'Tesla', fallbackWeight: 1.5 },
  { symbol: 'AMD', name: 'AMD', fallbackWeight: 0.8 },
];
const stockSymbols = stocks.map((stock) => stock.symbol);

const fallbackCryptoData = [
  { id: 'bitcoin', symbol: 'btc', name: 'Bitcoin', market_cap: 1200000000000, price_change_percentage_24h: null },
  { id: 'ethereum', symbol: 'eth', name: 'Ethereum', market_cap: 430000000000, price_change_percentage_24h: null },
  { id: 'solana', symbol: 'sol', name: 'Solana', market_cap: 90000000000, price_change_percentage_24h: null },
  { id: 'binancecoin', symbol: 'bnb', name: 'BNB', market_cap: 85000000000, price_change_percentage_24h: null },
  { id: 'uniswap', symbol: 'uni', name: 'Uniswap', market_cap: 8000000000, price_change_percentage_24h: null },
  { id: 'arbitrum', symbol: 'arb', name: 'Arbitrum', market_cap: 7000000000, price_change_percentage_24h: null },
  { id: 'render-token', symbol: 'rndr', name: 'Render', market_cap: 6000000000, price_change_percentage_24h: null },
];

function MarketHeatmapPage() {
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const [cryptoData, setCryptoData] = useState([]);
  const [stockQuotes, setStockQuotes] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      try {
        const [cryptoResult, stockResult] = await Promise.allSettled([
          fetch('/api/market/heatmap'),
          fetch(`/api/fundamentals?symbols=${encodeURIComponent(stockSymbols.join(','))}`),
        ]);
        if (cancelled) return;

        const quotes = {};
        if (cryptoResult.status === 'fulfilled' && cryptoResult.value.ok !== false) {
          const payload = await cryptoResult.value.json();
          const crypto = payload?.coins || payload;
          setCryptoData(Array.isArray(crypto) && crypto.length ? crypto : fallbackCryptoData);
        } else {
          setCryptoData(fallbackCryptoData);
        }
        if (stockResult.status === 'fulfilled' && stockResult.value.ok !== false) {
          const stockData = await stockResult.value.json();
          Object.entries(stockData?.data || {}).forEach(([symbol, quote]) => {
            quotes[symbol] = quote;
          });
        }
        setStockQuotes(quotes);
      } catch {
        if (!cancelled) {
          setCryptoData(fallbackCryptoData);
          setStockQuotes({});
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 120000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const sectorLabel = (key) => ({
    layer1: t('home.heatmapSectorLayer1'),
    defi: t('home.heatmapSectorDefi'),
    meme: t('home.heatmapSectorMeme'),
    l2: t('home.heatmapSectorL2'),
    gaming: t('home.heatmapSectorGaming'),
    ai: t('home.heatmapSectorAi'),
    other: t('home.heatmapSectorOther'),
  }[key] || key);

  const classifyCoin = (coin) => {
    for (const [sector, ids] of Object.entries(sectorMap)) {
      if (ids.includes(coin.id)) return sector;
    }
    return 'other';
  };

  const formatChange = (change) => {
    if (typeof change !== 'number') return t('home.heatmapFeatured');
    return `${change >= 0 ? '+' : ''}${change.toFixed(1)}%`;
  };

  const getHeatmapColor = (change) => {
    if (typeof change !== 'number') return 'rgba(212, 168, 67, 0.16)';
    if (change >= 10) return 'rgba(34, 197, 94, 0.88)';
    if (change >= 5) return 'rgba(34, 197, 94, 0.68)';
    if (change >= 2) return 'rgba(34, 197, 94, 0.48)';
    if (change >= 0) return 'rgba(34, 197, 94, 0.28)';
    if (change >= -2) return 'rgba(248, 113, 113, 0.42)';
    if (change >= -5) return 'rgba(248, 113, 113, 0.68)';
    if (change >= -10) return 'rgba(239, 68, 68, 0.78)';
    return 'rgba(220, 38, 38, 0.9)';
  };

  const groupedSectors = () => {
    const groups = {};
    cryptoData.forEach((coin) => {
      const key = classifyCoin(coin);
      if (!groups[key]) groups[key] = [];
      groups[key].push(coin);
    });

    return ['layer1', 'defi', 'meme', 'l2', 'ai', 'gaming', 'other']
      .filter((key) => groups[key]?.length)
      .map((key) => ({
        key,
        label: sectorLabel(key),
        assets: groups[key]
          .sort((a, b) => (b.market_cap || 0) - (a.market_cap || 0))
          .slice(0, key === 'other' ? 12 : 10),
      }));
  };

  const stockAssets = stocks.map((stock) => {
    const quote = stockQuotes[stock.symbol];
    return {
      id: stock.symbol,
      symbol: stock.symbol,
      name: quote?.shortName || quote?.longName || stock.name,
      change: typeof quote?.regularMarketChangePercent === 'number' ? quote.regularMarketChangePercent : null,
      weight: quote?.marketCap || stock.fallbackWeight,
    };
  });

  return (
    <main className="mh-page">
      <header className="mh-hero">
        <button className="mh-back" onClick={() => navigate(ROUTES.market)}>← {lang === 'zh' ? '返回市场研究院' : 'Back to Market'}</button>
        <p>{lang === 'zh' ? 'Full Heatmap' : 'Full Heatmap'}</p>
        <h1>{lang === 'zh' ? '完整市场热力图' : 'Full Market Heatmap'}</h1>
        <span>{lang === 'zh' ? '加密板块与美股观察列表，适合快速扫盘和发现强弱分化。' : 'Crypto sectors and US stock watchlist for quick market scanning.'}</span>
      </header>

      {loading ? (
        <div className="mh-loading">{t('home.heatmapLoading')}</div>
      ) : (
        <>
          <section className="mh-section">
            <div className="mh-section-head">
              <h2>{t('home.heatmapCryptoTitle')}</h2>
              <span>{t('home.heatmapCryptoDesc')}</span>
            </div>
            <div className="mh-sector-grid">
              {groupedSectors().map((sector) => (
                <article className="mh-sector" key={sector.key}>
                  <div className="mh-sector-head">
                    <strong>{sector.label}</strong>
                    <span>{sector.assets.length}</span>
                  </div>
                  <div className="mh-blocks">
                    {sector.assets.map((asset) => {
                      const change = asset.price_change_percentage_24h;
                      const flex = Math.max(1, Math.log2(((asset.market_cap || 1) / 1e8) + 1));
                      return (
                        <div
                          className="mh-block"
                          key={asset.id}
                          style={{ flex, backgroundColor: getHeatmapColor(change) }}
                          title={`${asset.name}: ${formatChange(change)}`}
                        >
                          <strong>{asset.symbol?.toUpperCase()}</strong>
                          <span>{formatChange(change)}</span>
                        </div>
                      );
                    })}
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="mh-section">
            <div className="mh-section-head">
              <h2>{t('home.heatmapStocksTitle')}</h2>
              <span>{t('home.heatmapStocksDesc')}</span>
            </div>
            <div className="mh-stock-grid">
              {stockAssets.map((asset) => (
                <article className="mh-stock-card" key={asset.id}>
                  <div>
                    <strong>{asset.symbol}</strong>
                    <span>{asset.name}</span>
                  </div>
                  <em className={typeof asset.change === 'number' && asset.change < 0 ? 'down' : 'up'}>
                    {formatChange(asset.change)}
                  </em>
                </article>
              ))}
            </div>
          </section>
        </>
      )}
    </main>
  );
}

export default MarketHeatmapPage;
